import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Body, FlexColumn, FormModal, InputField, SelectField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { required } from '@ubeya/shared/utils/form';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const Description = styled(Body)`
  color: ${({ theme }) => theme.colors.grey4};
`;

const PayoutAllBalancesModal = ({ onClose, payoutAllBalances, management, employeeBranches = [] }) => {
  const { t } = useContext(ConfigContext);
  const { branchesOptions } = useBranches();

  // BE resolves branchId arbitrarily on multi-branch employees — scope here so payroll posts deterministically.
  const branchPickerOptions = useMemo(() => {
    if (!employeeBranches?.length) return branchesOptions;
    const employeeBranchesSet = new Set(employeeBranches);
    return branchesOptions.filter(({ value }) => employeeBranchesSet.has(value));
  }, [branchesOptions, employeeBranches]);

  const initialValues = useMemo(() => {
    if (!branchPickerOptions.length) return undefined;
    const primaryBranchId = management?.primaryBranchId ?? employeeBranches?.[0] ?? branchPickerOptions[0]?.value;
    if (!primaryBranchId) return undefined;
    return { branchId: primaryBranchId };
  }, [branchPickerOptions, employeeBranches, management]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        await payoutAllBalances({
          assignmentId: management.employeeContractV2Id,
          branchId: values.branchId,
          reason: values.reason || undefined
        });
        onClose();
      } catch {
        // useOptimisticMutation already shows the toaster.
      }
    },
    [payoutAllBalances, management, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('employeeLeave_payoutAll_title')}
      confirmText={t('confirm')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <Description>{t('employeeLeave_payoutAll_body')}</Description>
        <SelectField
          name="branchId"
          title={t('employeeLeave_payoutAll_branch')}
          options={branchPickerOptions}
          validate={required}
          required
          usePortal
          isMulti={false}
          clearable
        />
        <InputField name="reason" title={t('employeeLeave_payoutAll_reason')} />
      </Container>
    </FormModal>
  );
};

export default PayoutAllBalancesModal;
